import { useCallback, useEffect, useRef, useState } from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { APP_SETTINGS } from '../../config/appSettings'
import { resolveLinkMetadataApiBaseUrl } from '../../links/linkMetadata'
import { getSupabaseClient } from '../../supabase/client'
import { getSupabaseConfig } from '../../supabase/config'
import { getNativePlatform } from '../../utils/nativeRuntime'

export type GoogleKeepImportJobState =
  | 'uploading' | 'scanning' | 'awaiting_confirmation' | 'importing'
  | 'canceling' | 'canceled' | 'completed' | 'failed'
  | 'rolling_back' | 'rolled_back'

export interface GoogleKeepImportJob {
  blocked_notes: number
  completed_at: string | null
  completed_images: number
  completed_notes: number
  completed_work_units: number
  created_at: string
  duplicate_notes: number
  error_code: string | null
  failed_notes: number
  issue_counts: Record<string, number>
  job_id: string
  phase: string
  skipped_trash: number
  source_bytes: number
  source_deleted_at: string | null
  source_kind: 'folder' | 'zip'
  started_at: string | null
  state: GoogleKeepImportJobState
  temporary_tag: string
  temporary_tag_removed_at: string | null
  total_images: number
  total_notes: number
  total_work_units: number
  updated_at: string
  warning_notes: number
}

export interface GoogleKeepUploadProgress {
  completedBytes: number
  totalBytes: number
}

export type GoogleKeepImportAction = 'cancel' | 'confirm' | 'delete-source' | 'remove-tag' | 'retry' | 'rollback'

function apiBaseUrl(): string {
  return resolveLinkMetadataApiBaseUrl({
    configuredBaseUrl: import.meta.env.VITE_API_BASE_URL,
    appEnvironment: getSupabaseConfig()?.environment ?? 'development',
    nativePlatform: getNativePlatform(window),
  })
}

async function authorizedRequest(path: string, init?: RequestInit): Promise<Response> {
  const client = await getSupabaseClient()
  const { data, error } = await client.auth.getSession()
  if (error || !data.session?.access_token) throw new Error('authentication-required')
  const headers = new Headers(init?.headers)
  headers.set('Authorization', `Bearer ${data.session.access_token}`)
  const response = await fetch(`${apiBaseUrl()}${path}`, { ...init, headers })
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: unknown } | null
    throw new Error(typeof body?.error === 'string' ? body.error : `import-request-${response.status}`)
  }
  return response
}

export async function uploadGoogleKeepArchive(
  archive: Blob,
  sourceKind: 'folder' | 'zip',
  onProgress?: (progress: GoogleKeepUploadProgress) => void,
): Promise<string> {
  if (archive.size <= 0 || archive.size > APP_SETTINGS.imports.googleKeep.archiveMaxBytes) {
    throw new Error('source-too-large')
  }
  const start = await authorizedRequest('/api/imports/google-keep/uploads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sourceBytes: archive.size, sourceKind }),
  }).then((response) => response.json() as Promise<{
    jobId: string
    partBytes: number
    uploadConcurrency: number
    uploadId: string
  }>)
  const partCount = Math.ceil(archive.size / start.partBytes)
  const parts = new Array<{ etag: string; partNumber: number }>(partCount)
  let nextPartIndex = 0
  let completedBytes = 0
  try {
    const worker = async () => {
      while (true) {
        const index = nextPartIndex
        nextPartIndex += 1
        if (index >= partCount) return
        const partNumber = index + 1
        const part = archive.slice(index * start.partBytes, Math.min((index + 1) * start.partBytes, archive.size))
        const result = await authorizedRequest(
          `/api/imports/google-keep/uploads/${start.jobId}/parts/${partNumber}?uploadId=${encodeURIComponent(start.uploadId)}`,
          { method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: part },
        ).then((response) => response.json() as Promise<{ etag: string; partNumber: number }>)
        parts[index] = result
        completedBytes += part.size
        onProgress?.({ completedBytes, totalBytes: archive.size })
      }
    }
    await Promise.all(Array.from(
      { length: Math.min(start.uploadConcurrency, partCount) },
      () => worker(),
    ))
    await authorizedRequest(`/api/imports/google-keep/uploads/${start.jobId}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parts, uploadId: start.uploadId }),
    })
    return start.jobId
  } catch (error) {
    await authorizedRequest(
      `/api/imports/google-keep/uploads/${start.jobId}?uploadId=${encodeURIComponent(start.uploadId)}`,
      { method: 'DELETE' },
    ).catch(() => undefined)
    throw error
  }
}

export interface GoogleKeepImportController {
  act(jobId: string, action: GoogleKeepImportAction): Promise<void>
  error: string | null
  jobs: GoogleKeepImportJob[]
  loaded: boolean
  loading: boolean
  refresh(): Promise<void>
}

export function useGoogleKeepImports(enabled: boolean): GoogleKeepImportController {
  const [jobs, setJobs] = useState<GoogleKeepImportJob[]>([])
  const [loaded, setLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inFlightRef = useRef<Promise<void> | null>(null)
  const retryDelayRef = useRef(0)
  const retryAtRef = useRef(0)
  const refresh = useCallback(async () => {
    if (!enabled) {
      setJobs([])
      setLoaded(false)
      setLoading(false)
      retryDelayRef.current = 0
      retryAtRef.current = 0
      return
    }
    if (!navigator.onLine || Date.now() < retryAtRef.current) return
    if (inFlightRef.current) return inFlightRef.current
    const request = (async () => {
      setLoading(true)
      try {
        const response = await authorizedRequest('/api/imports/google-keep/jobs')
        const body = await response.json() as { jobs: GoogleKeepImportJob[] }
        setJobs(body.jobs)
        setLoaded(true)
        setError(null)
        retryDelayRef.current = 0
        retryAtRef.current = 0
      } catch (requestError) {
        retryDelayRef.current = Math.min(
          APP_SETTINGS.imports.googleKeep.jobPollRetryMaxMs,
          retryDelayRef.current === 0
            ? APP_SETTINGS.imports.googleKeep.jobFallbackPollMs
            : retryDelayRef.current * 2,
        )
        retryAtRef.current = Date.now() + retryDelayRef.current
        setError(requestError instanceof Error ? requestError.message : 'import-jobs-unavailable')
      } finally {
        setLoading(false)
        inFlightRef.current = null
      }
    })()
    inFlightRef.current = request
    return request
  }, [enabled])

  const act = useCallback(async (jobId: string, action: GoogleKeepImportAction) => {
    await authorizedRequest(`/api/imports/google-keep/jobs/${jobId}/${action}`, { method: 'POST' })
    retryAtRef.current = 0
    await refresh()
  }, [refresh])

  useEffect(() => {
    if (!enabled) {
      setJobs([])
      setLoaded(false)
      setLoading(false)
      retryDelayRef.current = 0
      retryAtRef.current = 0
      return
    }
    void refresh()
    const catchUp = () => {
      if (navigator.onLine && document.visibilityState === 'visible') void refresh()
    }
    const reconnect = () => {
      retryAtRef.current = 0
      catchUp()
    }
    window.addEventListener('online', reconnect)
    window.addEventListener('focus', catchUp)
    window.addEventListener('pageshow', catchUp)
    document.addEventListener('visibilitychange', catchUp)
    const interval = window.setInterval(catchUp, APP_SETTINGS.imports.googleKeep.jobFallbackPollMs)
    let disposed = false
    let channel: RealtimeChannel | null = null
    void getSupabaseClient().then(async (client) => {
      if (disposed) return
      await client.realtime.setAuth()
      const config = getSupabaseConfig()
      return client.auth.getUser().then(({ data }) => {
        if (disposed || !data.user) return
        channel = client.channel(
          `minimany-import:${config?.environment ?? 'development'}:${data.user.id}`,
          { config: { private: true } },
        )
        channel.on('broadcast', { event: 'updated' }, () => { void refresh() }).subscribe()
      })
    }).catch(() => undefined)
    return () => {
      disposed = true
      window.clearInterval(interval)
      window.removeEventListener('online', reconnect)
      window.removeEventListener('focus', catchUp)
      window.removeEventListener('pageshow', catchUp)
      document.removeEventListener('visibilitychange', catchUp)
      if (channel) void getSupabaseClient().then((client) => client.removeChannel(channel!))
    }
  }, [enabled, refresh])

  return { act, error, jobs, loaded, loading, refresh }
}
